import {
    ThemeSelectors,
    TypeBeetle,
    TypeDiscovery,
    TypeFiesta,
    TypeMini,
    TypePrius,
    TypeSmart,
    TypeToledo,
} from '@at/design-system-foundation'
import styled, { css } from 'styled-components'
import { IconDealership, IconStarOn } from '@at/design-system-icons'
import { AccordionRow, Button } from '@at/design-system-elements'
import { AccordionList, Modal } from '@at/design-system-patterns'
import { BREAKPOINTS } from '@at/window-utils'

export const StyledSearchListingDetails = styled.section`
    display: flex;
    flex-direction: column;
    width: 100%;
    text-decoration: none;
    color: inherit;
    height: 100%;
    min-height: 0;
    justify-content: flex-start;
    @media (min-width: ${BREAKPOINTS.MEDIUM}px) {
        justify-content: space-between;
    }
`

export const StyledSearchListingTop = styled.div`
    & > a {
        text-decoration: none;
    }
    padding-bottom: ${({ theme }) => ThemeSelectors.getGridUnit(theme)};
`

export const StyledSearchListingContainer = styled.section`
    height: 100%;
    padding: ${({ theme }) => ThemeSelectors.getGridUnit(theme)};
`

export const StyledSearchListingDetailsTitle = styled.h3`
    ${({ theme }) => {
        return css`
            &::after {
                bottom: 0;
                content: '';
                left: 0;
                position: absolute;
                right: 0;
                top: 0;
                z-index: 1;
            }
            color: ${theme.color.text.title};
            margin: 0;
            text-decoration: none;
            transition: color 0.1s;
            font-size: 16px;
            font-variation-settings: 'wght' 500;
            line-height: 18px;
            min-height: 0;
            max-width: 100%;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            overflow: hidden;
            text-overflow: ellipsis;
        `
    }}
`

export const StyledSearchListingDetailsSubTitle = styled.p`
    color: ${({ theme }) => theme.color.text.default};
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`

export const AttentionGrabber = styled.p`
    margin-top: 4px;
    font-size: 14px;
`

export const StyledSearchListingLabelUL = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => ThemeSelectors.getLegacyGridUnit(theme) * 0.5}px;
    list-style-type: none;
    margin-bottom: 4px;
    padding: 0;
`
export const StyledLeasingListingFinanceInfo = styled.span`
    font-size: 12px;
    margin: 12px 0 4px 0;
`
export const StyledLeasingListingPriceInfo = styled.span<{ $useNewLeasingCard?: boolean }>`
    font-size: 12px;
    color: ${({ theme }) => theme.color.text.title};
    font-variation-settings: 'wght' ${({ $useNewLeasingCard }) => `${$useNewLeasingCard ? 400 : 500}`};
`
export const StyledLeasingListingPricePerMonth = styled.span`
    font-size: 18px;
    color: ${({ theme }) => theme.color.text.title};
    font-variation-settings: 'wght' 500;
`
export const StyledLeasingListingPriceWrapper = styled.div`
    margin-bottom: 0;
`
export const StyledLeasingListingWithRepExampleModalPriceWrapper = styled.div`
    margin-bottom: 0;
    position: relative;
    z-index: 3;
    display: flex;
    align-items: baseline;
`

export const StyledSearchFinanceWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: end;
`
export const StyledSearchListingPrice = styled(TypeToledo)<{ isFlexibleFinance?: boolean }>`
    ${({ theme, isFlexibleFinance }) => {
        return css`
            display: flex;
            justify-content: ${isFlexibleFinance ? 'space-between' : 'auto'};
            gap: ${!isFlexibleFinance && ThemeSelectors.getGroupFourSpacing(theme)};
            align-items: baseline;
            margin: 8px 0;
            @media (min-width: ${BREAKPOINTS.MEDIUM}px) {
                margin: 0;
            }
        `
    }}
`
export const StyledSearchListingPriceLabelWrapper = styled.div`
    z-index: 2;
    border-bottom: ${({ theme }) => theme.color.text.link.default} 1px solid;
    &:hover {
        border-bottom-color: ${({ theme }) => theme.color.text.link.hover}; /* Hover border-bottom color */
    }
`
export const StyledSearchListingPriceLabel = styled(TypeBeetle)<{
    isRrp?: boolean
    $isNewLeasingCard?: boolean
}>`
    z-index: 3;
    font-size: ${({ $isNewLeasingCard }) => `${$isNewLeasingCard ? 16 : 18}px`};
    padding-left: 0;
    padding-right: 0;
    line-height: ${({ $isNewLeasingCard }) => `${$isNewLeasingCard ? 24 : 18}px`};
    font-variation-settings: 'wght' 500;
    border-style: none;
    ${({ $isNewLeasingCard }) =>
        $isNewLeasingCard
            ? css`
                  border-bottom: 1px solid currentColor;
                  &:hover {
                      border-bottom-color: currentColor;
                  }
              `
            : null}
    background: none;
    cursor: pointer;
    color: ${({ theme }) => theme.color.text.link.default};
    &:hover {
        color: ${({ theme }) => theme.color.text.link.hover};
        & svg {
            fill: currentColor;
        }
    }
    display: flex;
    gap: ${({ theme }) => ThemeSelectors.getGroupOneSpacing(theme)};
    align-items: center;
`
export const StyledSpanWrapPrice = styled(TypeBeetle)`
    @media (max-width: 767px) {
        display: block;
    }
    color: ${({ theme }) => theme.color.text.title};
    font-size: 18px;
    font-variation-settings: 'wght' 500;
    line-height: 18px;
`
export const StyledSpanWrap = styled.small`
    @media (max-width: 767px) {
        display: block;
    }
`

export const StyledInfoModal = styled(Modal)`
    @media (max-width: 768px) {
        &.info-modal {
            & > section:nth-child(1) {
                position: relative;
                & > div:nth-child(1) {
                    position: sticky; //Makes the back button sticky on mobile
                    top: -24px;
                    background-color: ${({ theme }) => theme.color.bgSurface.default};
                    padding-top: 12px;
                    z-index: 20;
                }
            }
        }
    }
`

export const StyledFinanceModalContent = styled.div`
    display: flex;
    flex-direction: column;
    & p {
        margin-top: ${({ theme }) => ThemeSelectors.getGroupThreeSpacing(theme)};
    }
    & h2 {
        margin-top: 24px;
    }
`

export const FinanceModalTitle = styled(TypeDiscovery)`
    font-size: 24px;
    font-variation-settings: 'wght' 400;
    color: ${({ theme }) => theme.color.text.title};
`

export const FinanceModalParagraph = styled(TypeFiesta)`
    font-size: 16px;
    font-variation-settings: 'wght' 400;
    color: ${({ theme }) => theme.color.text.default};
`

export const FinanceModalSubtitle = styled(TypeFiesta)`
    font-size: 18px;
    font-variation-settings: 'wght' 500;
    color: ${({ theme }) => theme.color.text.title};
`

export const BoldText = styled.strong`
    font-variation-settings: 'wght' 500;
    color: ${({ theme }) => theme.color.text.default};
`

const SharedTextStyles = styled(TypeSmart)`
    display: inline;
    color: ${({ theme }) => theme.color.text.default};
    font-variation-settings: 'wght' 400;
    font-size: 14px;
`

export const LegalTextLink = styled(SharedTextStyles)`
    text-decoration: underline;
`

export const LegalTextParagraph = styled(SharedTextStyles)`
    margin-top: 16px;
`
export const LegalTitle = styled(LegalTextParagraph)`
    font-variation-settings: 'wght' 500;
`

export const StyledPriceAndRRPWrapper = styled.div`
    display: flex;
    flex-direction: column;
`

export const StyledLocationRatingWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    @media (max-width: ${BREAKPOINTS.MEDIUM}px) {
        margin-bottom: 4px;
    }
`

export const StyledRatingWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (min-width: ${BREAKPOINTS.MEDIUM}px) {
        margin-top: 8px;
    }
    flex-shrink: 0;
    margin-left: auto;
`
export const DealerRating = styled(TypeMini)`
    font-size: 12px;
    font-variation-settings: 'wght' 400;
    padding: 0 4px;
    color: ${({ theme }) => theme.color.text.default};
`
export const StyledStar = styled(IconStarOn)`
    height: 14px;
    width: 14px;
    margin-bottom: 3px;
`
export const StyledDealershipIcon = styled(IconDealership)`
    height: 14px;
    width: 14px;
    margin-bottom: 2px;
`

export const StyledWhatIsLeasingButton = styled(Button)`
    font-size: 12px;
    padding: 0;
    line-height: 16px;
    text-underline-offset: 5px;
    text-align: left;
    z-index: 2;
    position: relative;
    &:focus,
    &:focus-visible {
        outline: ${({ theme }) => `2px solid ${theme.color.border.focus}`};
    }
`

export const StyledPreRegSaving = styled.span`
    color: ${({ theme }) => theme.color.bgFill.notification};
    font-variation-settings: 'wght' 500;
`

export const StyledPreRegRRP = styled.span`
    text-decoration: line-through;
`

export const StyledPreRegSavingContainer = styled(TypeSmart)`
    display: flex;
    font-size: 14px;
    color: ${({ theme }) => theme.color.text.default};
    font-variation-settings: 'wght' 400;
    line-height: 16px;
    white-space: pre;
`
export const StyledVehicleLeaseOptionPart = styled.span`
    font-variation-settings: 'wght' 500;
`

export const StyledAcceptButton = styled(Button)`
    width: 180px;
    @media (max-width: ${BREAKPOINTS.MEDIUM}px) {
        width: 100%;
    }
    margin: auto;
    margin-top: ${({ theme }) => ThemeSelectors.getRelatedTwoSpacing(theme)};
`

export const StyledAccordionList = styled(AccordionList)`
    margin-top: ${({ theme }) => ThemeSelectors.getGroupFiveSpacing(theme)};
    border-top: 1px solid ${({ theme }) => theme.color.border.default};
`
export const StyledAccordionRow = styled(AccordionRow)`
    margin-top: ${({ theme }) => ThemeSelectors.getGroupFiveSpacing(theme)};
    border-top: 1px solid ${({ theme }) => theme.color.border.default};
`

export const StyledRRP = styled(TypePrius)`
    color: ${({ theme }) => theme.color.text.default};
    text-decoration: line-through;
    white-space: nowrap;
    font-size: 13px;
    font-variation-settings: 'wght' 400;
`

export const StyledDepositAndTerms = styled(TypeMini)`
    text-align: right;
    white-space: nowrap;
`
